/**
 * n8n Webhook Service
 * Handles communication with n8n webhooks for product processing via backend proxy
 */
import { apiBase } from './api';
import { supabase } from './supabase';

/**
 * Detect platform from URL
 * Categorizes into: amazon, etsy, alibaba, walmart, custom
 * @param {string} url - The product URL
 * @returns {string} - The detected platform
 */
export function detectPlatform(url) {
  if (!url) return 'custom';

  let host = '';
  try {
    host = new URL(url).hostname.toLowerCase();
  } catch {
    return 'custom';
  }

  // Amazon (any country TLD)
  if (host.includes('amazon.com') || host.startsWith('amazon.') || host.includes('.amazon.')) {
    return 'amazon';
  }

  // Etsy
  if (host.includes('etsy.com')) {
    return 'etsy';
  }

  // Alibaba group (Alibaba / AliExpress / 1688)
  if (
    host.includes('alibaba.com') ||
    host.includes('aliexpress.com') ||
    host.includes('1688.com')
  ) {
    return 'alibaba';
  }

  // Walmart
  if (host.includes('walmart.com') || host.includes('walmart.')) {
    return 'walmart';
  }

  // Everything else
  return 'custom';
}

/**
 * n8n Service for webhook communication
 */
export const n8nService = {
  /**
   * Submit a product URL to n8n for research processing
   * Proxied through backend: /api/submit-product
   *
   * @param {string} url - The product URL to process
   * @param {string} platform - The detected platform (optional)
   * @param {string} language - Output language code (e.g. en, fr, it, de)
   * @returns {Promise<Object>} - Response from n8n, including rawText for notifications
   */
  submitProduct: async (url, platform, language) => {
    // Auto-detect platform if not provided
    const detectedPlatform = platform || detectPlatform(url);

    try {
      // Attach the authenticated user's id so n8n can set products.user_id for RLS ownership.
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const user_id = user?.id || null;

      const response = await fetch(`${apiBase}/api/submit-product`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          url: url.trim(),
          platform: detectedPlatform,
          language: language || null,
          timestamp: new Date().toISOString(),
          user_id,
        }),
      });

      const rawText = await response.text();

      if (!response.ok) {
        throw new Error(`n8n webhook error: ${response.status} - ${rawText}`);
      }

      // Try to parse JSON response, but always return the raw text for notification parsing
      let data = null;
      try {
        data = JSON.parse(rawText);
      } catch {
        // Non-JSON response is fine
      }

      return {
        success: true,
        rawText,
        data,
      };
    } catch (error) {
      // Handle network errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to n8n. Please check your connection.');
      }
      throw error;
    }
  },

  /**
   * Check if n8n integration is configured
   * @returns {boolean}
   */
  isConfigured: () => {
    // With the backend proxy, the frontend only needs the /api route.
    // If the backend is running, integration is effectively configured.
    return true;
  },
};

export default n8nService;
